import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import Section from '../Section/Section';
import Spacer from '../Spacer/Spacer';
import Typography from '../Typography/Typography';
import Button from '../Button/Button';
import { Stack } from '../Stack/Stack';
import Table from '../Table/Table';
import type { LegalNoticeItem } from './consentTypes';

export type ConsentModalContentProps = {
  requiredLegalNotices: LegalNoticeItem[];
  getNoticeByIdentifier: (identifier: string) => LegalNoticeItem | undefined;
  onAccept: () => void;
  onDecline: () => void;
  canAccept: boolean;
};

const ConsentModalContent = ({
  requiredLegalNotices,
  getNoticeByIdentifier,
  onAccept,
  onDecline,
  canAccept,
}: ConsentModalContentProps) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAccept = () => {
    if (!canAccept) return;
    setIsSubmitting(true);
    onAccept();
  };

  return (
    <Section>
      <Spacer size={2} />
      <Typography align="left" style={{ maxWidth: '750px' }}>
        {t('legal_notices_dialog.description')}
      </Typography>
      <Spacer size={2} />
      <Typography align="left" component="div">
        <Trans
          i18nKey="legal_notices_dialog.link_intro"
          components={{
            termsLink: (
              <a
                className="link"
                href={
                  getNoticeByIdentifier('term')?.url ??
                  getNoticeByIdentifier('terms')?.url ??
                  '#'
                }
                target="_blank"
                rel="noopener noreferrer"
              />
            ),
            privacyLink: (
              <a
                className="link"
                href={getNoticeByIdentifier('privacy_policy')?.url ?? '#'}
                target="_blank"
                rel="noopener noreferrer"
              />
            ),
            cookieLink: (
              <a
                className="link"
                href={getNoticeByIdentifier('cookie_policy')?.url ?? '#'}
                target="_blank"
                rel="noopener noreferrer"
              />
            ),
          }}
        />
      </Typography>
      <Spacer size={1} />

      {requiredLegalNotices.length === 0 && (
        <Typography color="text.secondary">
          {t('legal_notices_dialog.loading')}
        </Typography>
      )}
      <Spacer size={2} />
      <div style={{ margin: '0 -8px' }}>
        <Table>
          <Table.Body hover={false}>
            <Table.Row>
              <Table.Cell>
                <strong>{t('legal_notices_dialog.new_user_label')}</strong>
              </Table.Cell>
              <Table.Cell>{t('legal_notices_dialog.new_user_text')}</Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>
                <strong>{t('legal_notices_dialog.existing_user_label')}</strong>
              </Table.Cell>
              <Table.Cell>
                {t('legal_notices_dialog.existing_user_text')}
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>
                <strong>{t('legal_notices_dialog.privacy_data_label')}</strong>
              </Table.Cell>
              <Table.Cell>
                {t('legal_notices_dialog.privacy_data_text')}
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </div>
      <Spacer size={4} />
      <Stack direction="row" gap={2}>
        <Button onClick={handleAccept} disabled={isSubmitting || !canAccept}>
          {isSubmitting
            ? t('legal_notices_dialog.saving')
            : t('legal_notices_dialog.accept')}
        </Button>
        <Button variant="text-light" color="default" onClick={onDecline}>
          {t('legal_notices_dialog.decline')}
        </Button>
      </Stack>
    </Section>
  );
};

export default ConsentModalContent;
